import i18n from '../../../../i18n';
import { formatHorizonNumber, formatServiceDate, metricAvailable } from './horizonFormat';
import { isOpenCell } from './horizonCalendar';
import type {
  HorizonComparison,
  HorizonCourseCell,
  HorizonOutlet,
  HorizonOutlook,
  HorizonOutlookDay,
  HorizonOutlookWindow,
  HorizonWindowForecast,
  HorizonWindowKey,
} from './horizonTypes';

export const WINDOW_KEYS: HorizonWindowKey[] = [
  'next7',
  'next14',
  'next30',
  'thisMonth',
  'nextMonth',
];
export const DEFAULT_WINDOW: HorizonWindowKey = 'next30';
export const FLEET_SCOPE = 'all';
/** Weeks the weekday heatmap reads ahead, whatever window is picked. */
export const HEATMAP_WEEKS = 8;

export type OutlookTone = 'ahead' | 'behind' | 'level' | 'neutral';

export function normalizeWindowKey(raw: string | null | undefined): HorizonWindowKey {
  return WINDOW_KEYS.includes(raw as HorizonWindowKey)
    ? (raw as HorizonWindowKey)
    : DEFAULT_WINDOW;
}

export function outlookWindows(
  outlook: HorizonOutlook | undefined,
  scopeId: string | null,
): HorizonOutlookWindow[] {
  if (!outlook) return [];
  return outlook.windows[scopeId ?? FLEET_SCOPE] ?? [];
}

export function outlookWindow(
  outlook: HorizonOutlook | undefined,
  scopeId: string | null,
  key: HorizonWindowKey,
): HorizonOutlookWindow | null {
  return outlookWindows(outlook, scopeId).find(w => w.key === key) ?? null;
}

function num(value: number): string {
  return formatHorizonNumber(Math.round(value));
}

function signedNum(value: number): string {
  const n = Math.round(value);
  return n > 0 ? `+${num(n)}` : num(n);
}

function signedPct(value: number): string {
  const n = Math.round(value);
  return n > 0 ? `+${n}%` : `${n}%`;
}

export function comparisonTone(comp: HorizonComparison | null | undefined): OutlookTone {
  if (!comp || !comp.available) return 'neutral';
  if (comp.tone === 'ahead' || comp.tone === 'behind' || comp.tone === 'level') {
    return comp.tone;
  }
  return 'neutral';
}

export type OutlookFigure = {
  key: string;
  label: string;
  value: string;
  detail: string;
  tone: OutlookTone;
};

function comparisonFigure(
  key: 'lastYear' | 'recentWeeks',
  comp: HorizonComparison,
): OutlookFigure {
  const label = i18n.t(`horizon:outlook.${key}`);
  if (!comp.available || comp.vsPct == null) {
    const detail =
      comp.unavailableReason === 'not_comparable'
        ? i18n.t('horizon:outlook.lyNotComparable', {
            then: num(comp.lyServed ?? 0),
            now: num(comp.recentServed ?? 0),
          })
        : i18n.t(key === 'lastYear' ? 'horizon:outlook.noLastYear' : 'horizon:outlook.noRecent');
    return { key, label, value: '—', detail, tone: 'neutral' };
  }
  const detailKey =
    key === 'lastYear' ? 'horizon:outlook.lastYearDetail' : 'horizon:outlook.recentDetail';
  const final =
    key === 'lastYear' && comp.final
      ? ` ${i18n.t('horizon:outlook.lastYearFinal', { covers: num(comp.final) })}`
      : '';
  return {
    key,
    label,
    value: signedPct(comp.vsPct),
    detail:
      i18n.t(detailKey, {
        then: num(comp.samePoint),
        now: num(comp.bookedComparable),
      }) + final,
    tone: comparisonTone(comp),
  };
}

export function forecastReasonText(reason: string | null | undefined): string {
  switch (reason) {
    case 'calibrating':
      return i18n.t('horizon:outlook.forecastCalibrating');
    case 'not_accurate_enough':
      return i18n.t('horizon:outlook.forecastNotAccurate');
    case 'no_comparable_history':
      return i18n.t('horizon:outlook.forecastNoHistory');
    case 'forecast_disabled':
      return i18n.t('horizon:outlook.forecastOff');
    case 'no_open_dates':
      return i18n.t('horizon:outlook.forecastNoOpen');
    default:
      return i18n.t('horizon:outlook.forecastUnavailable');
  }
}

function forecastFigure(
  forecast: HorizonWindowForecast | undefined,
  enabled: boolean,
): OutlookFigure {
  const label = i18n.t('horizon:outlook.forecast');
  if (!enabled) {
    return { key: 'forecast', label, value: '—', detail: forecastReasonText('forecast_disabled'), tone: 'neutral' };
  }
  if (!forecast || !forecast.available || forecast.value == null) {
    const partial =
      forecast?.readyDays && forecast.openDays
        ? ` ${i18n.t('horizon:outlook.forecastPartial', {
            ready: forecast.readyDays,
            total: forecast.openDays,
          })}`
        : '';
    return {
      key: 'forecast',
      label,
      value: '—',
      detail: forecastReasonText(forecast?.reason) + partial,
      tone: 'neutral',
    };
  }
  const ranged = forecast.low != null && forecast.high != null;
  return {
    key: 'forecast',
    label,
    value: `~${num(forecast.value)}`,
    detail: ranged
      ? i18n.t('horizon:outlook.forecastRange', {
          low: num(forecast.low as number),
          high: num(forecast.high as number),
        })
      : i18n.t('horizon:outlook.forecastNoRange'),
    tone: 'neutral',
  };
}

/**
 * The key numbers for one window, in reading order. Every figure carries its
 * own sentence so nothing hides behind a click.
 */
export function outlookFigures(
  window: HorizonOutlookWindow,
  { forecastEnabled }: { forecastEnabled: boolean },
): OutlookFigure[] {
  const week = window.momentum.week;
  const open =
    window.capacity > 0 ? Math.max(0, Math.round(window.capacity - window.booked)) : null;
  return [
    {
      key: 'booked',
      label: i18n.t('horizon:outlook.booked'),
      value: num(window.booked),
      detail: i18n.t('horizon:outlook.bookedDetail', {
        count: window.bookings,
        reservations: num(window.bookings),
      }),
      tone: 'neutral',
    },
    {
      key: 'full',
      label: i18n.t('horizon:outlook.full'),
      value: window.occupancyPct != null ? `${Math.round(window.occupancyPct)}%` : '—',
      detail:
        open != null
          ? i18n.t('horizon:outlook.fullDetail', { open: num(open) })
          : i18n.t('horizon:outlook.noCapacity'),
      tone: 'neutral',
    },
    comparisonFigure('lastYear', window.lastYear),
    comparisonFigure('recentWeeks', window.recentWeeks),
    {
      key: 'momentum',
      label: i18n.t('horizon:outlook.momentum'),
      value: week != null ? signedNum(week) : '—',
      detail:
        week == null
          ? i18n.t('horizon:outlook.momentumNone')
          : week >= 0
            ? i18n.t('horizon:outlook.momentumUp')
            : i18n.t('horizon:outlook.momentumDown'),
      tone: week == null || Math.abs(week) < 1 ? 'neutral' : week > 0 ? 'ahead' : 'behind',
    },
    forecastFigure(window.forecast, forecastEnabled),
  ];
}

/**
 * Sum a figure across outlets for one date, or null unless every outlet that
 * carries the date has it. A partial sum would read as a weak reference.
 */
function sumAll(rows: HorizonOutlookDay[], key: keyof HorizonOutlookDay): number | null {
  if (rows.length === 0) return null;
  let total = 0;
  for (const row of rows) {
    const value = row[key];
    if (typeof value !== 'number') return null;
    total += value;
  }
  return total;
}

function groupByDate<T extends { serviceDate: string }>(rows: T[]): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const row of rows) {
    const list = out.get(row.serviceDate);
    if (list) list.push(row);
    else out.set(row.serviceDate, [row]);
  }
  return out;
}

export type ShapePoint = {
  serviceDate: string;
  label: string;
  weekday: number;
  booked: number;
  lastYear: number | null;
  recent: number | null;
  capacity: number | null;
  forecast: number | null;
  /** [low, high] for the range area; null when no range is published. */
  range: [number, number] | null;
  holiday: boolean;
  closed: boolean;
  lowConfidence: boolean;
};

/**
 * One point per date for the shape chart: the book now, where last year and
 * recent weeks stood at the same lead, the seats available, and the forecast
 * range where the accuracy gate lets it through.
 */
export function buildShapeSeries(
  outlook: HorizonOutlook | undefined,
  cells: HorizonCourseCell[],
  scopeId: string | null,
): ShapePoint[] {
  if (!outlook) return [];
  const days = scopeId ? outlook.days.filter(d => d.outletId === scopeId) : outlook.days;
  const scopedCells = scopeId ? cells.filter(c => c.outletId === scopeId) : cells;
  const cellsByDate = groupByDate(scopedCells);
  const out: ShapePoint[] = [];
  for (const [serviceDate, rows] of [...groupByDate(days).entries()].sort(([a], [b]) =>
    a.localeCompare(b),
  )) {
    const dayCells = cellsByDate.get(serviceDate) ?? [];
    const open = dayCells.filter(isOpenCell);
    if (dayCells.length === 0) continue;
    let capacity: number | null = null;
    for (const cell of open) {
      if (metricAvailable(cell.coverCapacity)) {
        capacity = (capacity ?? 0) + (cell.coverCapacity.value as number);
      }
    }
    const projected = open.every(c => metricAvailable(c.projected));
    const banded = open.every(c => c.band.p20 != null && c.band.p80 != null);
    const forecast =
      open.length > 0 && projected
        ? open.reduce((s, c) => s + (c.projected.value as number), 0)
        : null;
    const range: [number, number] | null =
      forecast != null && banded
        ? [
            open.reduce((s, c) => s + (c.band.p20 as number), 0),
            open.reduce((s, c) => s + (c.band.p80 as number), 0),
          ]
        : null;
    out.push({
      serviceDate,
      label: formatServiceDate(serviceDate),
      weekday: rows[0].weekday,
      booked: rows.reduce((s, r) => s + r.booked, 0),
      lastYear: sumAll(rows, 'lySamePoint'),
      recent: sumAll(rows, 'recentSamePoint'),
      capacity,
      forecast: forecast != null ? Math.round(forecast * 10) / 10 : null,
      range,
      holiday: dayCells.some(c => c.holiday),
      closed: open.length === 0,
      lowConfidence: open.length > 0 && open.every(c => c.beyondConfidence),
    });
  }
  return out;
}

export type DayTone = 'ahead' | 'behind' | 'level' | 'none';

export type DayVerdict = {
  reference: number | null;
  vsPct: number | null;
  tone: DayTone;
};

/** A single date swings more than a stretch, so its "about the same" band is wider. */
export const DAY_LEVEL_PCT = 10;
/** Below this many comparable guests a percentage says more about noise than demand. */
export const MIN_DAY_REFERENCE = 5;

/**
 * Judge each date against one comparison for the whole chart: last year at the
 * same point when any date has it, recent weeks otherwise. Mixing the two
 * across bars would make neighbouring dates incomparable.
 */
export function dayVerdicts(
  points: ShapePoint[],
  preferred: 'lastYear' | 'recentWeeks' | null = null,
): {
  basis: 'lastYear' | 'recentWeeks' | null;
  verdicts: DayVerdict[];
} {
  const basis =
    preferred ??
    (points.some(p => p.lastYear != null)
      ? 'lastYear'
      : points.some(p => p.recent != null)
        ? 'recentWeeks'
        : null);
  const verdicts = points.map((point): DayVerdict => {
    const reference = basis === 'lastYear' ? point.lastYear : basis ? point.recent : null;
    if (point.closed || reference == null || reference < MIN_DAY_REFERENCE) {
      return { reference: point.closed ? null : reference, vsPct: null, tone: 'none' };
    }
    const vsPct = Math.round((point.booked / reference - 1) * 100);
    const tone: DayTone =
      vsPct >= DAY_LEVEL_PCT ? 'ahead' : vsPct <= -DAY_LEVEL_PCT ? 'behind' : 'level';
    return { reference, vsPct, tone };
  });
  return { basis, verdicts };
}

/** The chart follows the stretch picked above, not the whole booking window. */
export function pointsInWindow(
  points: ShapePoint[],
  window: HorizonOutlookWindow | null,
): ShapePoint[] {
  if (!window) return points.slice(0, 30);
  return points.filter(p => p.serviceDate >= window.start && p.serviceDate <= window.end);
}

export type HeatCell = {
  serviceDate: string;
  booked: number;
  reference: number | null;
  vsPct: number | null;
};

export type HeatRow = {
  key: string;
  label: string;
  /** Always seven, Monday first; null where the row has no such date. */
  cells: (HeatCell | null)[];
};

export type WeekdayHeatmap = {
  basis: 'lastYear' | 'recentWeeks' | null;
  rows: HeatRow[];
  /** Per weekday across every week shown. */
  totals: (HeatCell | null)[];
};

/** Below this reference a percentage says more about noise than demand. */
const MIN_HEAT_REFERENCE = 5;

function heatCell(serviceDate: string, booked: number, reference: number | null): HeatCell {
  return {
    serviceDate,
    booked,
    reference,
    vsPct:
      reference != null && reference >= MIN_HEAT_REFERENCE
        ? Math.round((booked / reference - 1) * 100)
        : null,
  };
}

/**
 * Weeks down, weekdays across: each date's book against the same point last
 * year, or recent weeks when there is no last year. Shows where the gaps sit.
 */
export function buildWeekdayHeatmap(
  outlook: HorizonOutlook | undefined,
  scopeId: string | null,
  weeks = HEATMAP_WEEKS,
  preferred: 'lastYear' | 'recentWeeks' | null = null,
): WeekdayHeatmap {
  if (!outlook) return { basis: null, rows: [], totals: new Array(7).fill(null) };
  const days = (scopeId ? outlook.days.filter(d => d.outletId === scopeId) : outlook.days).filter(
    d => d.daysAhead < weeks * 7,
  );
  const byDate = groupByDate(days);
  const hasLy = days.some(d => d.lySamePoint != null);
  const hasRecent = days.some(d => d.recentSamePoint != null);
  const basis = preferred ?? (hasLy ? 'lastYear' : hasRecent ? 'recentWeeks' : null);
  const refKey: keyof HorizonOutlookDay = basis === 'recentWeeks' ? 'recentSamePoint' : 'lySamePoint';

  const rows: HeatRow[] = [];
  const weekdayBooked = new Array(7).fill(0);
  const weekdayRef = new Array(7).fill(0);
  const weekdaySeen = new Array(7).fill(false);
  const weekdayRefOk = new Array(7).fill(true);
  let current: HeatRow | null = null;
  for (const [serviceDate, rowsForDate] of [...byDate.entries()].sort(([a], [b]) =>
    a.localeCompare(b),
  )) {
    const weekday = rowsForDate[0].weekday;
    const index = Math.min(6, Math.max(0, weekday - 1));
    if (!current || index === 0 || current.cells[index] !== null) {
      current = {
        key: serviceDate,
        label: i18n.t('horizon:patterns.weekOf', { date: formatServiceDate(serviceDate) }),
        cells: new Array(7).fill(null),
      };
      rows.push(current);
    }
    const booked = rowsForDate.reduce((s, r) => s + r.booked, 0);
    const reference = basis ? sumAll(rowsForDate, refKey) : null;
    current.cells[index] = heatCell(serviceDate, booked, reference);
    weekdaySeen[index] = true;
    weekdayBooked[index] += booked;
    if (reference == null) weekdayRefOk[index] = false;
    else weekdayRef[index] += reference;
  }
  const totals = weekdaySeen.map((seen, i) =>
    seen
      ? heatCell(`weekday-${i + 1}`, weekdayBooked[i], weekdayRefOk[i] ? weekdayRef[i] : null)
      : null,
  );
  return { basis, rows, totals };
}

export const DAYPART_ORDER = ['Morning', 'Lunch', 'Afternoon', 'Dinner', 'Late'];

export type DaypartShare = { key: string; label: string; covers: number; share: number };

export function buildDaypartSplit(
  outlook: HorizonOutlook | undefined,
  scopeId: string | null,
  window: HorizonOutlookWindow | null,
): DaypartShare[] {
  if (!outlook || !window) return [];
  const totals = new Map<string, number>();
  for (const day of outlook.days) {
    if (scopeId && day.outletId !== scopeId) continue;
    if (day.serviceDate < window.start || day.serviceDate > window.end) continue;
    for (const [part, covers] of Object.entries(day.dayparts)) {
      totals.set(part, (totals.get(part) ?? 0) + covers);
    }
  }
  const sum = [...totals.values()].reduce((s, v) => s + v, 0);
  if (sum <= 0) return [];
  return [...totals.entries()]
    .filter(([, covers]) => covers > 0)
    .sort(
      ([a], [b]) =>
        (DAYPART_ORDER.indexOf(a) + 1 || 99) - (DAYPART_ORDER.indexOf(b) + 1 || 99),
    )
    .map(([key, covers]) => ({
      key,
      label: i18n.t(`horizon:daypart.${key}`, { defaultValue: key }),
      covers,
      share: (covers / sum) * 100,
    }));
}

export type OutletRow = {
  outletId: string;
  name: string;
  window: HorizonOutlookWindow;
};

/** Most behind first, so the outlet that needs a push leads the table. */
export function buildOutletRows(
  outlook: HorizonOutlook | undefined,
  outlets: HorizonOutlet[],
  key: HorizonWindowKey,
): OutletRow[] {
  if (!outlook) return [];
  const rows: OutletRow[] = [];
  for (const outlet of outlets) {
    const window = outlookWindow(outlook, outlet.id, key);
    if (window) rows.push({ outletId: outlet.id, name: outlet.name, window });
  }
  const pace = (row: OutletRow): number | null => {
    const basis = row.window.primaryBasis;
    return basis ? row.window[basis].vsPct : null;
  };
  return rows.sort((a, b) => {
    const pa = pace(a);
    const pb = pace(b);
    if (pa == null && pb == null) return b.window.booked - a.window.booked;
    if (pa == null) return 1;
    if (pb == null) return -1;
    return pa - pb || b.window.booked - a.window.booked;
  });
}

/** The dates a window spans, for filtering per-date data to it. */
export function windowDates(window: HorizonOutlookWindow | null): Set<string> {
  const out = new Set<string>();
  if (!window) return out;
  const [y, m, d] = window.start.split('-').map(Number);
  const cursor = new Date(Date.UTC(y, m - 1, d));
  for (let i = 0; i < window.days; i += 1) {
    out.add(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
}
